// src/components/admin/AdminSlides.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';
import ImageUploader from './ImageUploader';

const EMPTY_SLIDE = {
  title: '',
  eyebrow: '',
  image: '',
  buttonText: 'Shop Now',
  link: '/',
  duration: 5,
  active: true,
  sort_order: 0,
};

const LINK_PRESETS = [
  { label: 'Home', value: '/' },
  { label: 'New Arrivals', value: '/#new-arrivals' },
  { label: 'CBD Flower', value: '/#cbd-flower' },
  { label: 'Hemp Resin', value: '/#hemp-resin' },
  { label: 'Vapes & Carts', value: '/#vapes-carts' },
  { label: 'Bundles', value: '/#bundles' },
  { label: 'Blog', value: '/blog' },
  { label: 'Loyalty', value: '/loyalty' },
];

function SlideForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_SLIDE);
  const [uploadError, setUploadError] = useState('');
  const { showToast } = useApp();

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{
      background: '#111', border: '1px solid rgba(76,202,116,.2)',
      borderRadius: 16, padding: 24, marginBottom: 16,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Title */}
        <div style={{ gridColumn: '1 / -1' }}>
          <div className="input-label">Slide Title</div>
          <input
            className="input-field"
            value={form.title}
            onChange={e => update('title', e.target.value)}
            placeholder="Premium CBD Flower Collection"
          />
        </div>

        {/* Eyebrow */}
        <div>
          <div className="input-label">Eyebrow (small text above title)</div>
          <input
            className="input-field"
            value={form.eyebrow}
            onChange={e => update('eyebrow', e.target.value)}
            placeholder="Just Dropped"
          />
        </div>

        {/* Button text */}
        <div>
          <div className="input-label">Button Text</div>
          <input
            className="input-field"
            value={form.buttonText}
            onChange={e => update('buttonText', e.target.value)}
            placeholder="Shop Now"
          />
        </div>

        {/* Link */}
        <div>
          <div className="input-label">Button Link</div>
          <select
            className="input-field"
            value={LINK_PRESETS.some(p => p.value === form.link) ? form.link : '__custom'}
            onChange={e => {
              if (e.target.value !== '__custom') update('link', e.target.value);
            }}
            style={{ appearance: 'none' }}
          >
            {LINK_PRESETS.map(p => (
              <option key={p.value} value={p.value}>{p.label} ({p.value})</option>
            ))}
            <option value="__custom">Custom URL…</option>
          </select>
          {!LINK_PRESETS.some(p => p.value === form.link) && (
            <input
              className="input-field"
              style={{ marginTop: 8 }}
              value={form.link}
              onChange={e => update('link', e.target.value)}
              placeholder="/custom-path"
            />
          )}
        </div>

        {/* Duration */}
        <div>
          <div className="input-label">Display Duration (seconds)</div>
          <input
            className="input-field"
            type="number"
            min={2} max={30}
            value={form.duration}
            onChange={e => update('duration', Number(e.target.value))}
          />
        </div>

        {/* Sort order */}
        <div>
          <div className="input-label">Sort Order</div>
          <input
            className="input-field"
            type="number"
            min={0}
            value={form.sort_order}
            onChange={e => update('sort_order', Number(e.target.value))}
          />
        </div>

        {/* Active toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={e => update('active', e.target.checked)}
              style={{ accentColor: '#4cca74', width: 16, height: 16 }}
            />
            <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.82rem', color: '#ccc' }}>
              Active (visible on homepage)
            </span>
          </label>
        </div>
      </div>

      {/* Image upload — uses dedicated 'slides' bucket */}
      <div style={{ marginTop: 16 }}>
        <div className="input-label">Slide Banner Image</div>

        {uploadError && (
          <div className="upload-error">
            {uploadError}
            <code>
              In Supabase: Storage → New bucket → Name: "slides" → Public: ✓ → Create
            </code>
          </div>
        )}

        <ImageUploader
          bucket="slides"
          folder="hero"
          value={form.image}
          onChange={(url) => { update('image', url); setUploadError(''); }}
          onError={(msg) => setUploadError(msg)}
          label="Upload Banner"
          preview="banner"
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
        <button
          className="btn-primary"
          onClick={() => onSave(form)}
          style={{ minHeight: 44, minWidth: 120 }}
        >
          Save Slide
        </button>
        <button
          onClick={onCancel}
          style={{
            background: 'none', border: '1px solid rgba(255,255,255,.12)',
            borderRadius: 25, padding: '10px 20px', color: '#666',
            fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.85rem',
            fontWeight: 700, cursor: 'pointer', minHeight: 44,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function AdminSlides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | 'new' | slide object
  const { showToast } = useApp();

  const fetchSlides = async () => {
    const { data } = await supabase
      .from('hero_slides')
      .select('*')
      .order('sort_order');
    setSlides(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchSlides(); }, []);

  const handleSave = async (form) => {
    const payload = {
      title: form.title,
      eyebrow: form.eyebrow,
      image: form.image,
      button_text: form.buttonText,
      link: form.link,
      duration: form.duration,
      active: form.active,
      sort_order: form.sort_order,
    };
    let err;
    if (editing === 'new') {
      ({ error: err } = await supabase.from('hero_slides').insert(payload));
    } else {
      ({ error: err } = await supabase.from('hero_slides').update(payload).eq('id', editing.id));
    }
    if (err) { showToast('Save failed: ' + err.message, 'error'); return; }
    showToast('Slide saved', 'success');
    setEditing(null);
    fetchSlides();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this slide?')) return;
    await supabase.from('hero_slides').delete().eq('id', id);
    showToast('Slide deleted', 'info');
    fetchSlides();
  };

  const toggleActive = async (slide) => {
    await supabase.from('hero_slides').update({ active: !slide.active }).eq('id', slide.id);
    fetchSlides();
  };

  // Map DB column names to component prop names for edit form
  const toFormShape = (slide) => ({
    ...slide,
    buttonText: slide.button_text,
  });

  if (loading) return (
    <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );

  return (
    <div className="page-container slide-manager" style={{ padding: '32px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2rem', letterSpacing: '.05em', marginBottom: 4 }}>
            Homepage Slides
          </h2>
          <p style={{ fontSize: '.82rem', color: '#555' }}>
            Manage the hero slideshow shown on the homepage. Drag to reorder (use sort_order field).
          </p>
        </div>
        {!editing && (
          <button
            className="btn-primary"
            onClick={() => setEditing('new')}
            style={{ minHeight: 44, whiteSpace: 'nowrap' }}
          >
            + Add Slide
          </button>
        )}
      </div>

      {/* New slide form */}
      {editing === 'new' && (
        <SlideForm
          initial={{ ...EMPTY_SLIDE, sort_order: slides.length }}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Existing slides */}
      {slides.length === 0 && !editing && (
        <div style={{
          background: '#111', border: '1px solid rgba(255,255,255,.07)',
          borderRadius: 16, padding: '40px 24px', textAlign: 'center', color: '#444',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: 12, opacity: .3 }}>🖼️</div>
          <p style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.9rem', letterSpacing: '.1em', textTransform: 'uppercase' }}>
            No slides yet. Click "Add Slide" to create the first one.
          </p>
          <p style={{ fontSize: '.78rem', marginTop: 8, color: '#333' }}>
            Until slides are added, the homepage shows default placeholder slides.
          </p>
        </div>
      )}

      {slides.map(slide => (
        <div key={slide.id}>
          {editing && editing !== 'new' && editing.id === slide.id ? (
            <SlideForm
              initial={toFormShape(slide)}
              onSave={handleSave}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div className="slide-card">
              {/* Thumbnail */}
              <div className="slide-thumb">
                {slide.image
                  ? <img src={slide.image} alt={slide.title} />
                  : <span>🖼️</span>
                }
              </div>

              {/* Info */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{
                    background: slide.active ? 'rgba(76,202,116,.12)' : 'rgba(255,255,255,.05)',
                    border: `1px solid ${slide.active ? 'rgba(76,202,116,.3)' : 'rgba(255,255,255,.1)'}`,
                    borderRadius: 20, padding: '2px 10px',
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: '.65rem', fontWeight: 700, letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: slide.active ? '#4cca74' : '#555',
                  }}>
                    {slide.active ? 'Active' : 'Hidden'}
                  </span>
                  <span style={{ fontSize: '.72rem', color: '#444' }}>
                    Order: {slide.sort_order} · {slide.duration}s
                  </span>
                </div>
                <div style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: '1rem', fontWeight: 700, color: '#f4f4f4', marginBottom: 2,
                }}>
                  {slide.title || <span style={{ color: '#444', fontStyle: 'italic' }}>No title</span>}
                </div>
                <div style={{ fontSize: '.78rem', color: '#555' }}>
                  {slide.eyebrow && <span style={{ color: '#4cca74' }}>{slide.eyebrow} · </span>}
                  Button: "{slide.button_text}" → {slide.link}
                </div>
              </div>

              {/* Actions */}
              <div className="slide-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                <button
                  onClick={() => setEditing(toFormShape(slide))}
                  style={{
                    background: 'none', border: '1px solid rgba(255,255,255,.1)',
                    borderRadius: 20, padding: '8px 16px', color: '#888',
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.78rem',
                    fontWeight: 700, cursor: 'pointer', minHeight: 40, whiteSpace: 'nowrap',
                  }}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => toggleActive(slide)}
                  style={{
                    background: 'none',
                    border: `1px solid ${slide.active ? 'rgba(240,165,0,.3)' : 'rgba(76,202,116,.3)'}`,
                    borderRadius: 20, padding: '8px 16px',
                    color: slide.active ? '#f0a500' : '#4cca74',
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.78rem',
                    fontWeight: 700, cursor: 'pointer', minHeight: 40, whiteSpace: 'nowrap',
                  }}
                >
                  {slide.active ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => handleDelete(slide.id)}
                  style={{
                    background: 'none', border: '1px solid rgba(224,80,80,.25)',
                    borderRadius: 20, padding: '8px 16px', color: '#e05050',
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.78rem',
                    fontWeight: 700, cursor: 'pointer', minHeight: 40,
                  }}
                >
                  🗑 Delete
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* SQL hint */}
      <div style={{
        marginTop: 32, background: '#0d0d0d', border: '1px solid rgba(255,255,255,.06)',
        borderRadius: 12, padding: 20, fontSize: '.78rem', color: '#444',
      }}>
        <div style={{ color: '#4cca74', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.72rem', fontWeight: 700, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
          Supabase Setup
        </div>
        <p style={{ marginBottom: 8 }}>Run this SQL once to create the hero_slides table:</p>
        <pre style={{ background: '#0a0a0a', padding: 12, borderRadius: 8, overflowX: 'auto', fontSize: '.72rem', color: '#4cca74', fontFamily: 'monospace' }}>{`CREATE TABLE hero_slides (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  title text,
  eyebrow text,
  image text,
  button_text text DEFAULT 'Shop Now',
  link text DEFAULT '/',
  duration integer DEFAULT 5,
  active boolean DEFAULT true,
  sort_order integer DEFAULT 0,
  created_at timestamptz DEFAULT now()
);
ALTER TABLE hero_slides ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public read" ON hero_slides FOR SELECT USING (true);
CREATE POLICY "Admin write" ON hero_slides FOR ALL USING (
  EXISTS (SELECT 1 FROM profiles WHERE id = auth.uid() AND role = 'admin')
);`}</pre>
      </div>
    </div>
  );
}